import {
  AppstoreAddOutlined,
  CopyOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Grid,
  message,
  Modal,
  Pagination,
  Progress,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Tabs, { CompatibilityProps } from 'antd/lib/tabs';
import {
  InfrastructureInstance,
  InfrastructureInstanceState,
  InfrastructureInstanceStatusEnum,
} from 'osep-generator-service-client-v1';
import { Page } from 'osep-react-antd';
import { Tab } from 'rc-tabs/lib/interface';
import React from 'react';
import { SiJira } from 'react-icons/si';
import { useNavigate } from 'react-router';
import packageJson from '../../package.json';
import DeleteInfraProjectInstanceButton from '../components/DeleteInfraProjectInstanceButton';
import DeprovisionInfraProjectInstanceButton from '../components/DeprovisionInfraProjectInstanceButton';
import Markdown from '../components/Markdown';
import ProvisionInfraProjectInstanceButton from '../components/ProvisionInfraProjectInstanceButton';
import useInfraInstanceState from '../hooks/query/useInfraInstanceState';
import useInfraInstancesPage from '../hooks/query/useInfraInstancesPage';
import useIsAdmin from '../hooks/useIsAdmin';
import { ConfigModel } from '../iasConfigSchema';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import mapGitLabPipelineStatus from '../utils/mapGitLabPipelineStatus';
import ErrorPage from './ErrorPage';
import useInfraProjectConfigPage from '../hooks/query/useInfraProjectConfigsPage';

const ContentActions: React.FC<{
  content: string;
  fileName: string;
  mimeType: string;
  label: string;
}> = ({ content, fileName, mimeType, label }) => (
  <Space>
    <Tooltip title={`Copy ${label} to clipboard`}>
      <Button
        icon={<CopyOutlined />}
        onClick={async () => {
          await navigator.clipboard.writeText(content);

          message.success(`Copied ${label} to clipboard.`);
        }}
      >
        Copy
      </Button>
    </Tooltip>
    <Tooltip title={`Download ${label} as ${fileName}`}>
      <Button
        icon={<DownloadOutlined />}
        onClick={() => {
          const url = URL.createObjectURL(
            new Blob([content], { type: mimeType }),
          );
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
        }}
      >
        Download
      </Button>
    </Tooltip>
  </Space>
);

const InstanceInfoModalTabs: React.FC<{
  instance: InfrastructureInstance;
  state: InfrastructureInstanceState;
}> = ({ instance, state }) => {
  const fileNamePrefix = instance.name.toLowerCase().replace(/ /g, '-');
  const items: (Omit<Tab, 'destroyInactiveTabPane'> & CompatibilityProps)[] =
    [];

  if (state.generatedReadmeContent) {
    items.push({
      key: 'readme',
      label: 'Readme',

      children: (
        <Card
          extra={
            <ContentActions
              content={state.generatedReadmeContent}
              fileName={`${fileNamePrefix}-readme.md`}
              mimeType="text/markdown"
              label="Readme"
            />
          }
        >
          <Markdown>{state.generatedReadmeContent}</Markdown>
        </Card>
      ),
    });
  }

  if (state.generatedStateContent) {
    const stateJson = JSON.stringify(state.generatedStateContent, null, 2);

    items.push({
      key: 'state',
      label: 'State',
      children: (
        <Card
          extra={
            <ContentActions
              content={stateJson}
              fileName={`${fileNamePrefix}-state.json`}
              mimeType="application/json"
              label="State"
            />
          }
        >
          <pre>{stateJson}</pre>
        </Card>
      ),
    });
  }

  if (items.length === 0) {
    return <Empty />;
  }

  return <Tabs items={items} size="large" />;
};

const InstanceInfoModal: React.FC<{
  instance: InfrastructureInstance;
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ instance, open, setOpen }) => {
  const { data, isLoading } = useInfraInstanceState(instance.id, open);

  return (
    <Modal
      loading={isLoading}
      open={open}
      onCancel={() => setOpen(false)}
      footer={
        <Button type="primary" onClick={() => setOpen(false)}>
          Close
        </Button>
      }
      width={'auto'}
    >
      {open && data && (
        <InstanceInfoModalTabs instance={instance} state={data} />
      )}
    </Modal>
  );
};

const InstanceInfo: React.FC<{
  instance: InfrastructureInstance;
}> = ({ instance }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  return (
    <>
      <InstanceInfoModal
        instance={instance}
        open={isModalOpen}
        setOpen={setIsModalOpen}
      />
      <Tooltip key="info-link" title="Show Instance Info">
        <InfoCircleOutlined
          type="primary"
          onClick={() => setIsModalOpen(!isModalOpen)}
        />
      </Tooltip>
    </>
  );
};

const useInstancesAndProjectConfigs = () => {
  const {
    data: instancesPage,
    error: instancesError,
    isLoading: areInstancesLoading,
  } = useInfraInstancesPage({}, 3000);

  const projectIds = React.useMemo(
    () =>
      Array.from(
        new Set(instancesPage?.instances.map((i) => i.projectId) ?? []),
      ).sort(),
    [instancesPage],
  );

  const {
    data: projectConfigsPage,
    error: projectConfigsError,
    isLoading: areProjectConfigsLoading,
  } = useInfraProjectConfigPage(
    {
      projectIds,
    },
    !!projectIds?.length,
  );

  const projectConfigsByProjectId = React.useMemo(
    () =>
      new Map(
        projectConfigsPage?.projectConfigs.map((projectConfig) => [
          projectConfig.projectId,
          projectConfig,
        ]),
      ),
    [projectConfigsPage],
  );

  const instancesAndProjectConfigs = React.useMemo(() => {
    if (!instancesPage?.instances || !projectConfigsPage) {
      return [];
    }

    return instancesPage.instances.flatMap((instance) => {
      const projectConfig = projectConfigsByProjectId.get(instance.projectId);

      if (!projectConfig) {
        console.error(
          `Missing project config for projectId ${instance.projectId}`,
        );

        return [];
      }

      return {
        instance,
        projectConfig,
      };
    });
  }, [projectConfigsByProjectId, instancesPage?.instances, projectConfigsPage]);

  return {
    data: instancesAndProjectConfigs,
    error: instancesError ?? projectConfigsError,
    isLoading: areInstancesLoading || areProjectConfigsLoading,
  };
};

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [{ page, pageSize }, setPage] = React.useState({
    page: 1,
    pageSize: 10,
  });

  const { isAdmin } = useIsAdmin();

  const screen = Grid.useBreakpoint();

  const {
    data: instancesAndProjectConfigs,
    error: instancesAndProjectConfigsError,
    isLoading: areInstancesAndProjectConfigsLoading,
  } = useInstancesAndProjectConfigs();

  if (instancesAndProjectConfigsError) {
    return <ErrorPage error={instancesAndProjectConfigsError} />;
  }

  return (
    <Page
      title="Infrastructure"
      isLoading={areInstancesAndProjectConfigsLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
      onBack={() => navigate('/')}
    >
      <Button
        type="primary"
        onClick={() => navigate(`/ias/add`)}
        style={{ width: screen.xs ? '100%' : 'auto' }}
        size="large"
        icon={<AppstoreAddOutlined />}
      >
        Provision Infrastructure
      </Button>
      <Divider />
      <Row gutter={[16, 16]}>
        {instancesAndProjectConfigs.map(({ instance, projectConfig }) => {
          const config = projectConfig.content as ConfigModel;
          const templates = config.templates;
          const permissions =
            projectConfig.effectivePermissions[instance.templateName];

          const gitlabPipeline = instance.gitlabPipelines.at(-1);
          const gitlabPipelineStatus = gitlabPipeline
            ? mapGitLabPipelineStatus(gitlabPipeline.status)
            : undefined;
          const jiraIssue = instance.jiraIssues.find(
            (issue) => issue.gitlabPipelineId === gitlabPipeline?.id,
          );

          const actions: React.ReactNode[] = [];

          if (
            gitlabPipelineStatus &&
            gitlabPipelineStatus.status === 'SUCCESS' &&
            instance.latestStatusChange.status ===
              InfrastructureInstanceStatusEnum.Provisioned
          ) {
            actions.push(<InstanceInfo instance={instance} />);
          }

          if (isAdmin && gitlabPipeline) {
            actions.push(
              <Tooltip key="gitlab-pipeline-link" title="Open GitLab Pipeline">
                <LinkOutlined
                  type="primary"
                  onClick={() =>
                    window.open(gitlabPipeline.url, '_blank')?.focus()
                  }
                  disabled={instance.gitlabPipelines.length === 0}
                />
              </Tooltip>,
            );
          }

          if (jiraIssue) {
            actions.push(
              <Tooltip key="jira-ticket-link" title="Open Jira Ticket">
                <span
                  onClick={() => window.open(jiraIssue.url, '_blank')?.focus()}
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {SiJira({})}
                </span>
              </Tooltip>,
            );
          }

          if (
            gitlabPipelineStatus &&
            gitlabPipelineStatus.status !== 'RUNNING' &&
            instance.latestStatusChange.status ===
              InfrastructureInstanceStatusEnum.Provisioned
          ) {
            if (permissions?.delete_instance) {
              actions.push(
                <DeprovisionInfraProjectInstanceButton instance={instance} />,
              );
            }
          }

          if (
            gitlabPipelineStatus &&
            gitlabPipelineStatus.status !== 'RUNNING' &&
            instance.latestStatusChange.status ===
              InfrastructureInstanceStatusEnum.Deprovisioned
          ) {
            if (permissions?.create_instance) {
              actions.push(
                <ProvisionInfraProjectInstanceButton instance={instance} />,
              );
            }
            if (permissions?.delete_instance) {
              actions.push(
                <DeleteInfraProjectInstanceButton instance={instance} />,
              );
            }
          }

          const template = templates.find(
            (t) => t.name === instance.templateName,
          );

          if (!template) {
            return null;
          }

          return (
            <Col key={instance.id} xs={24} sm={24} md={12} lg={8} xl={6}>
              <Card
                title={
                  <Typography.Title level={4}>
                    {template.display_name}
                  </Typography.Title>
                }
                key={instance.id}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                cover={
                  <Space style={{ textAlign: 'center' }}>
                    <img
                      src={findImageByProjectNameOrType(template.name)}
                      alt={`${template.name} icon`}
                      style={{
                        height: 100,
                        width: 'auto',
                        marginTop: 20,
                        filter:
                          instance.latestStatusChange.status ===
                          InfrastructureInstanceStatusEnum.Deprovisioned
                            ? 'grayscale(1)'
                            : 'none',
                      }}
                    />
                  </Space>
                }
                actions={actions}
              >
                <Card.Meta
                  title={
                    <Space
                      direction="horizontal"
                      style={{
                        paddingBottom: 12,
                      }}
                    >
                      {instance.name}
                      <Space.Compact>
                        <Space.Addon>{instance.id.slice(-5)}</Space.Addon>
                        <Tooltip title="Copy Instance ID">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={async () => {
                              await navigator.clipboard.writeText(instance.id);

                              message.success('Copied ID to clipboard.');
                            }}
                          />
                        </Tooltip>
                      </Space.Compact>
                    </Space>
                  }
                  description={
                    <>
                      {instance.latestStatusChange.status ===
                        InfrastructureInstanceStatusEnum.Deprovisioned && (
                        <Tag color="grey">Deprovisioned</Tag>
                      )}
                      {instance.latestStatusChange.status ===
                        InfrastructureInstanceStatusEnum.Provisioning && (
                        <Tag
                          icon={
                            <SyncOutlined
                              spin={gitlabPipelineStatus?.status === 'RUNNING'}
                            />
                          }
                        >
                          Provisioning
                        </Tag>
                      )}
                      {instance.latestStatusChange.status ===
                        InfrastructureInstanceStatusEnum.Provisioned && (
                        <Tag color="green">Deployed</Tag>
                      )}
                      {instance.latestStatusChange.status ===
                        InfrastructureInstanceStatusEnum.Deprovisioning && (
                        <Tag
                          icon={
                            <SyncOutlined
                              spin={gitlabPipelineStatus?.status === 'RUNNING'}
                            />
                          }
                        >
                          Deprovisioning
                        </Tag>
                      )}
                      {gitlabPipeline &&
                        [gitlabPipeline].map((gitlabPipeline) => {
                          const { progressStatus } = mapGitLabPipelineStatus(
                            gitlabPipeline.status,
                          );

                          const percent =
                            progressStatus === 'active'
                              ? gitlabPipeline.progress - 20
                              : gitlabPipeline.progress;

                          const format =
                            progressStatus === 'active'
                              ? (percent?: number) => {
                                  if (gitlabPipeline.progress < 100) {
                                    return (
                                      <>
                                        {instance.latestStatusChange.status ===
                                          InfrastructureInstanceStatusEnum.Provisioning &&
                                          'Creating...'}
                                        {instance.latestStatusChange.status ===
                                          InfrastructureInstanceStatusEnum.Deprovisioned &&
                                          'Deleting...'}
                                      </>
                                    );
                                  }

                                  if (gitlabPipeline.progress === 100) {
                                    return (
                                      <>
                                        {instance.latestStatusChange.status ===
                                          InfrastructureInstanceStatusEnum.Provisioning &&
                                          'Provisioning...'}
                                        {instance.latestStatusChange.status ===
                                          InfrastructureInstanceStatusEnum.Deprovisioned &&
                                          'Deprovisioning...'}
                                      </>
                                    );
                                  }
                                }
                              : undefined;

                          return (
                            <Progress
                              percent={percent}
                              key={gitlabPipeline.id}
                              status={progressStatus}
                              style={{ paddingTop: 24, width: '100%' }}
                              format={format}
                              percentPosition={
                                progressStatus === 'active'
                                  ? {
                                      align: 'center',
                                      type: 'outer',
                                    }
                                  : undefined
                              }
                              size={'small'}
                            />
                          );
                        })}
                    </>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      <Pagination
        defaultCurrent={page}
        pageSize={pageSize}
        onChange={(page, pageSize) => setPage({ page, pageSize })}
        pageSizeOptions={['10', '20', '50', '100']}
        showSizeChanger
        align="center"
        style={{ marginTop: 24 }}
      />
    </Page>
  );
};

export default ProjectsPage;
